import { isEmpty } from 'lodash';
import { QueryKey, QueryObserverBaseResult, useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { checkAuth, CheckAuthOptions, useAuth } from './auth-utils';

export function isAnyProperty(
  property: keyof QueryObserverBaseResult<unknown, unknown>,
  queries: UseQueryResult<unknown, unknown>[],
) {
  return queries.some((query) => query[property]);
}

export function isAllEmpty(...queries: UseQueryResult<unknown, unknown>[]) {
  return queries.every((query) => isEmpty(query.data));
}

/**
 * You will need to pass in the types for the return values of data from the queries in a tuple.
 *
 * @param queries
 * @returns Object with the data property merged from all queries - and is properties are true if any are true
 */
export function combineQueryResults<
  TData extends unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TQueries extends UseQueryResult<TData>[] = UseQueryResult<any>[],
>(...queries: TQueries) {
  const data = queries.map((query) => query.data);

  return {
    data: data as TData | undefined,
    isLoading: isAnyProperty('isLoading', queries),
    isFetching: isAnyProperty('isFetching', queries),
    isSuccess: isAnyProperty('isSuccess', queries),
  };
}

export function useAuthQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  check: CheckAuthOptions['type'],
): UseQueryResult<TData, TError> {
  const auth = useAuth();
  const isAuth = checkAuth({ type: check, auth });
  const enabled = options.enabled !== undefined ? isAuth && options.enabled : isAuth;
  return useQuery<TQueryFnData, TError, TData, TQueryKey>({ ...options, enabled });
}

export function queryKey<TArgs extends unknown[], TReturn extends unknown[]>(
  name: string,
  fn?: (...args: TArgs) => TReturn,
) {
  return (...args: TArgs) => {
    const value = fn ? fn(...args) : [true as unknown];
    return [
      {
        [name]: value,
      },
    ] as QueryKey;
  };
}

export function combineKeys<TArgs extends unknown[], TKeys extends QueryKey[]>(fn: (...args: TArgs) => TKeys) {
  return (...args: TArgs) => {
    const keys = fn(...args);

    const keyObj = keys.reduce((acc, value) => {
      const key = value[0];

      if (typeof key === 'object') {
        return {
          ...acc,
          ...key,
        };
      }

      return {
        ...acc,
        key,
      };
    }, {});

    return [keyObj] as QueryKey;
  };
}
